import {useEffect, useRef} from 'react'
import {useCustomerType} from '@salesforce/commerce-sdk-react'
import {useCurrentCustomer} from '@salesforce/retail-react-app/app/hooks/use-current-customer'
import {buildOptimizelyAttributes, normalizeCustomerGroups} from '../utils/optimizely-attributes'
import {getVisitorId} from '../utils/optimizely-visitor-id'
import {setFxTrackingContext, trackFxEvent} from '../utils/optimizely-fx-events'
import {useOptimizelyFxConfig} from './use-optimizely-fx-config'

/*
 * Keeps the FX tracking context (enabled, sdkKey, visitor id, session
 * attributes) in sync with the session. Visitor-level attributes (device,
 * referrer, geo…) are merged at send time by optimizely-fx-events. Values are
 * consumed only inside effects, so SSR/hydration output is untouched.
 */
export function useOptimizelyFxTracking() {
    const {enabled, sdkKey, brandSlug, localeId} = useOptimizelyFxConfig()
    const {customerType, isRegistered} = useCustomerType()
    const {data: customer} = useCurrentCustomer()
    const customerGroupsCsv = normalizeCustomerGroups(customer?.c_customerGroups).join(',')
    const previousType = useRef(null)

    useEffect(() => {
        setFxTrackingContext({
            enabled,
            sdkKey,
            userId: getVisitorId() || 'anonymous',
            attributes: buildOptimizelyAttributes({
                brandSlug,
                localeId,
                isLoggedIn: !!isRegistered,
                customerGroups: customerGroupsCsv ? customerGroupsCsv.split(',') : []
            })
        })
    }, [enabled, sdkKey, brandSlug, localeId, isRegistered, customerGroupsCsv])

    // login_success / logout_success: the stored customer type flipping between
    // guest and registered. The first non-null read is the page-load state.
    useEffect(() => {
        if (customerType !== 'guest' && customerType !== 'registered') return
        const previous = previousType.current
        previousType.current = customerType
        if (!previous || previous === customerType) return
        trackFxEvent(customerType === 'registered' ? 'login_success' : 'logout_success', null, {
            URL: window.location.href
        })
    }, [customerType])
}
